<script>
export default {
	props: {
		items: Array,
		currentIndex: Number,
		showDots: Boolean,
		showArrows: Boolean,
		noDrag: Boolean,
		swipeSensitivity: {
			type: Number,
			default: 10,
		},
	},
	model: {
		prop: 'currentIndex',
		event: 'update:currentIndex',
	},
	data() {
		return {
			touchStartX: 0,
			touchStartY: 0,
			touchEndX: 0,
			touchEndY: 0,
			isSwiping: false,
			isVerticalSwipe: false,
			direction: null,
		}
	},
	computed: {
		currentItem() {
			return this.items[this.currentIndex]
		},
		getHandlers() {
			if (this.$b.d || this.noDrag || this.items.length == 1) return {}
			return {
				touchstart: this.touchStart,
				touchmove: this.touchMove,
				touchend: this.touchEnd,
			}
		},
		displayDots() {
			const dots = []

			for (let i = 0; i < this.items.length; i++) {
				dots.push({
					index: i,
					distance: Math.abs(this.currentIndex - i),
				})
			}

			return dots
		},
	},
	methods: {
		next() {
			if (this.currentIndex < this.items.length - 1) {
				this.$emit('update:currentIndex', this.currentIndex + 1)
			} else {
				this.$emit('update:currentIndex', 0)
			}
		},
		prev() {
			if (this.currentIndex > 0) {
				this.$emit('update:currentIndex', this.currentIndex - 1)
			} else {
				this.$emit('update:currentIndex', this.items.length - 1)
			}
		},
		touchStart(event) {
			this.touchStartX = event.touches[0].clientX
			this.touchStartY = event.touches[0].clientY
			this.isSwiping = false
			this.isVerticalSwipe = false
		},
		touchMove(event) {
			if (event.touches.length == 1) {
				this.touchEndX = event.touches[0].clientX
				this.touchEndY = event.touches[0].clientY

				const diffX = this.touchStartX - this.touchEndX
				const diffY = this.touchStartY - this.touchEndY

				if (Math.abs(diffX) > this.swipeSensitivity || Math.abs(diffY) > this.swipeSensitivity) {
					this.isSwiping = true
					this.direction = diffX < 0 ? 'left' : 'right'

					if (Math.abs(diffY) > Math.abs(diffX)) {
						this.isVerticalSwipe = true
					} else {
						event.preventDefault()
					}
				}
			}
		},
		touchEnd(event) {
			if (this.isSwiping && !this.isVerticalSwipe) {
				const diffX = this.touchStartX - this.touchEndX

				if (diffX < -this.swipeSensitivity) {
					this.prev()
				} else if (diffX > this.swipeSensitivity) {
					this.next()
				}
				this.isSwiping = false
			}
		},
		getDotSizeClass(distance) {
			const maxDistance = 4
			const normalizedDistance = Math.min(distance, maxDistance)

			const sizeClasses = ['dot-size-0', 'dot-size-1', 'dot-size-2', 'dot-size-3', 'dot-size-4']

			return sizeClasses[normalizedDistance]
		},
		getKey(dot) {
			const key = `dot-${dot.index}`
			return key
		},
	},
}
</script>

<template>
	<div class="content-switcher" :class="{ '--mobile': $b.mt }">
		<div class="__content-wrapper h100 w100">
			<div
				class="__content h100"
				:class="{
					'--swiping': isSwiping && !isVerticalSwipe,
					'--left': direction == 'left',
					'--right': direction == 'right',
				}"
				ref="content"
				v-on="getHandlers"
				:key="currentIndex"
			>
				<slot name="default" :item="currentItem" />
			</div>
			<slot name="back-arrow">
				<div v-if="showArrows && items.length > 1" icon class="__arrow --back" @click="prev">
					<v-icon class="mdi mdi-chevron-left base--text" color="black"></v-icon>
				</div>
			</slot>
			<slot name="next-arrow">
				<div v-if="showArrows && items.length > 1" icon class="__arrow --next" @click="next">
					<v-icon class="mdi mdi-chevron-right base--text" color="black"></v-icon>
				</div>
			</slot>
		</div>

		<slot name="dots" :current-index="currentIndex">
			<div
				v-if="showDots && items.length > 1"
				class="__dots-wrapper z1 mt-2 d-flex justify-center align-center w100"
			>
				<div class="__dots px-2 d-flex justify-center align-center w100">
					<div
						v-for="(dot, index) in displayDots"
						:key="getKey(dot)"
						@click="$emit('update:currentIndex', index)"
						:class="[
							'__dot',
							getDotSizeClass(dot.distance),
							{ '--active': dot.index === currentIndex },
						]"
					></div>
				</div>
			</div>
		</slot>
	</div>
</template>

<style scoped lang="scss">
@keyframes fadeIn {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}

.content-switcher {
	position: relative;
	.__swiper {
		width: 100%;
		height: 80%;
		position: absolute;
		top: 0;
		left: 0;
		z-index: 999;
		user-select: none;
		touch-action: pan-y;
	}

	.__content {
		animation: fadeIn 0.25s ease-in-out;
		&.--swiping {
			transition: transform 0.3s ease-in-out;
			&.--right {
				transform: translateX(-10%);
			}
			&.--left {
				transform: translateX(10%);
			}
		}
	}
	.__arrow {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		z-index: 1;
		cursor: pointer;
		background-color: #ffffff;
		box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.19);
		border-radius: 50%;

		&.--back {
			left: 2rem;
		}
		&.--next {
			right: 2rem;
		}
		::v-deep .v-icon {
			font-size: 40px;
		}
	}
	.__dots-wrapper {
		pointer-events: none;
		gap: 0.5rem;

		.__dots {
			background-color: rgba(255, 255, 255, 0.4);
			display: flex;
			justify-content: center;
			align-items: center;
			gap: 6px;
		}

		.__dot {
			border-radius: 10%;
			transition: all 0.3s ease;
			background-color: var(--link);
			opacity: 0.3;
			flex-grow: 1;

			&.--active {
				opacity: 1;
			}

			&.dot-size-0 {
				width: 100%;
				height: 8px;
				max-width: 200px;
			}

			&.dot-size-1 {
				width: 80%;
				height: 6px;
				max-width: 160px;
			}

			&.dot-size-2 {
				width: 70%;
				height: 6px;
				max-width: 140px;
			}

			&.dot-size-3 {
				width: 60%;
				height: 4px;
				max-width: 120px;
			}

			&.dot-size-4 {
				width: 50%;
				height: 4px;
				max-width: 100px;
			}
		}
	}

	&.--mobile {
		.__dots {
			height: 8px;
		}
	}
}
</style>

